import { ErrorBoundary } from '@bookjane2/bookjane-design-library';
import { CreateShiftButton } from 'components/CreateShiftButton';
import { DesktopPageHeader } from 'components/DesktopPageHeader';
import { Flex } from 'components/Flex';
import { MobilePageHeader } from 'components/MobilePageHeader/MobilePageHeader';
import { PageWrapper } from 'components/PageWrapper';
import { SettingsNavigation } from 'components/SettingsNavigation';
import { SetupBanner } from 'components/SetupBanner/SetupBanner';
import { useDimensionsRef } from 'hooks';
import { LocationSettingsPageRedirect } from 'pages/Settings/LocationSettingsPageRedirect';
import { SettingsLandingPage } from 'pages/Settings/SettingsLandingPage';
import { LocationSettingsPage } from 'pages/Settings/pages/LocationSettings';
import { SettingsPageViewTemplate } from 'pages/Settings/SettingsPage.styled';
import { FC, Fragment } from 'react';
import { Route, RouteProps, Switch, useRouteMatch } from 'react-router-dom';
import { MountTransition } from 'routes';
import { PositionsSettingsPage } from 'pages/Settings/pages/Positions';
import { PositionsSettingsRedirectPage } from 'pages/Settings/pages/Positions/PositionsSettingsPageRedirectPage';
import { OrganizationSettingsRedirectPage } from 'pages/Settings/pages/Organization/OrganizationSettingsRedirectPage';
import { SessionService } from 'services';
import { WhiteLabelAccessor } from 'utils/whiteLabelAccessor';
import { AdministratorSettingsPage } from 'pages/Settings/pages/Administrator/AdministratorSettingsPage';
import { AdministratorSettingsRedirectPage } from 'pages/Settings/pages/Administrator';
import { ShiftDistributionRulesTieringModal } from 'pages/Settings/modals/ShiftDistributionRulesTieringModal';
import { GroupsModal } from 'pages/Settings/modals/GroupsModal';
import { useTranslation } from 'hooks/useTranslation';
import { UserProfilePage } from 'pages/Settings/pages/UserProfile';
import { AccushieldDashboardButton } from 'components/AccushieldDashboardButton';
import { CompanyProfilePage } from 'pages/Settings/pages/CompanyProfile';
import { ADPProfilePage } from 'pages/Settings/pages/ADP/ADPProfilePage';
import { ADPPage } from 'pages/Settings/pages/ADP/ADPAuthRedirectPage/ADPPage';
import { PositionsAllocationModal } from 'pages/Settings/pages/Positions/B2BPositionsSettingsPage/modals/PositionsByLocation';
import { RemovePositionModal } from 'pages/Settings/pages/LocationSettings/PositionsAllocation/RemovePositionModal';
import { OrganizationSettingsPage } from 'pages/Settings/pages/Organization';
import { useDocumentTitle } from 'hooks/useDocumentTitle';

const SettingsModals: FC = () => {
  const isB2bUser = SessionService.assertUserType('B2B');
  return (
    <Fragment>
      <GroupsModal />
      <RemovePositionModal />
      <ShiftDistributionRulesTieringModal />
      {isB2bUser && <PositionsAllocationModal />}
    </Fragment>
  );
};

const SettingsPage: FC<RouteProps> = () => {
  const [ref, { width: x }] = useDimensionsRef<HTMLDivElement>();
  const { t } = useTranslation();
  useDocumentTitle(t('page_titles:SETTINGS'));
  const { isAccushieldProvider } = WhiteLabelAccessor;
  return (
    <PageWrapper>
      <SetupBanner />
      <DesktopPageHeader title={t('settings:SETTINGS')}>
        <Flex flexDirection="row-reverse" width="100%" gap="5px">
          {isAccushieldProvider && <AccushieldDashboardButton />}
          <CreateShiftButton />
        </Flex>
      </DesktopPageHeader>
      <MobilePageHeader title={t('settings:SETTINGS')} />
      <Flex width="100%" height="100%">
        <SettingsNavigation ref={ref} />
        <SettingsModals />
        <PageViewTemplate x={x} />
      </Flex>
    </PageWrapper>
  );
};

const PageViewTemplate: FC<{ x: number }> = ({ x }) => {
  const { path } = useRouteMatch();
  const isPendingApproval = SessionService.getUser()?.access_status === 'not_approved';

  return (
    <SettingsPageViewTemplate x={x} flexDirection="column" isPendingApproval={isPendingApproval}>
      <ErrorBoundary
        componentName="SettingsModule"
        suppressMessage
        showLogo={!WhiteLabelAccessor.isWhiteLabelled()}
      >
        <MountTransition>
          <Switch>
            <Route exact path={`${path}`} component={SettingsLandingPage} />
            <Route exact path={`${path}/locations`} component={LocationSettingsPageRedirect} />
            <Route path={`${path}/locations/:locationId`} component={LocationSettingsPage} />
            <Route exact path={`${path}/positions`} component={PositionsSettingsRedirectPage} />
            <Route path={`${path}/positions/:id`} component={PositionsSettingsPage} />
            <Route
              exact
              path={`${path}/administrators`}
              component={AdministratorSettingsRedirectPage}
            />
            <Route exact path="/settings/user" component={UserProfilePage} />
            <Route
              exact
              path="/settings/organization"
              component={OrganizationSettingsRedirectPage}
            />
            <Route path="/settings/organization" component={OrganizationSettingsPage} />
            <Route exact path="/settings/company" component={CompanyProfilePage} />
            <Route path={`${path}/administrators/:id`} component={AdministratorSettingsPage} />
            <Route exact path="/settings/adp" component={ADPPage} />
            <Route exact path="/settings/adp/profile" component={ADPProfilePage} />
          </Switch>
        </MountTransition>
      </ErrorBoundary>
    </SettingsPageViewTemplate>
  );
};

export default SettingsPage;
